import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "../cn";

const control =
  "w-full rounded-[var(--radius-control)] border border-line-strong bg-paper px-3 text-sm text-ink shadow-[var(--shadow-xs)] placeholder:text-ink-3 transition-[border-color,box-shadow] " +
  "hover:border-ink-3 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15 disabled:bg-subtle disabled:text-ink-3 disabled:shadow-none " +
  "aria-[invalid=true]:border-danger aria-[invalid=true]:focus:ring-danger/15";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={cn(control, "h-9", className)} {...rest} />;
});

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Accessible names for the reveal toggle (the app passes translated strings). */
  showLabel?: string;
  hideLabel?: string;
}

/** Password input with an eye button that toggles the value between hidden and visible. */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  { className, showLabel = "Show password", hideLabel = "Hide password", disabled, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;
  return (
    <div className="relative">
      <input
        ref={ref}
        type={visible ? "text" : "password"}
        disabled={disabled}
        className={cn(control, "h-9 pr-10", className)}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        aria-label={visible ? hideLabel : showLabel}
        aria-pressed={visible}
        title={visible ? hideLabel : showLabel}
        className="absolute inset-y-0 right-0 flex w-9 cursor-pointer items-center justify-center rounded-r-[var(--radius-control)] text-ink-3 hover:text-ink focus-visible:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed"
      >
        <Icon aria-hidden className="size-4" />
      </button>
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cn(control, "min-h-24 py-2", className)} {...rest} />;
  },
);

export const NativeSelect = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function NativeSelect({ className, children, ...rest }, ref) {
    return (
      <select
        ref={ref}
        className={cn(
          control,
          "select-chevron h-9 cursor-pointer pr-9 disabled:cursor-not-allowed",
          className,
        )}
        {...rest}
      >
        {children}
      </select>
    );
  },
);

export function Label({
  htmlFor,
  children,
  className,
  required,
}: {
  htmlFor?: string;
  children: ReactNode;
  className?: string;
  required?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className={cn("mb-1.5 block text-sm font-medium text-ink", className)}>
      {children}
      {required && (
        <span aria-hidden className="ml-0.5 text-danger">
          *
        </span>
      )}
    </label>
  );
}

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  className?: string;
  /** Render prop receives the generated id and aria props for the control. */
  children: (props: {
    id: string;
    "aria-invalid": boolean;
    "aria-describedby"?: string;
    required?: boolean;
  }) => ReactNode;
}

/** Label + control + hint + error with correct aria wiring. */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint ? hintId : "", error ? errId : ""].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cn("mb-4", className)}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      {children({
        id,
        "aria-invalid": Boolean(error),
        ...(describedBy ? { "aria-describedby": describedBy } : {}),
        ...(required ? { required: true } : {}),
      })}
      {hint && !error && (
        <p id={hintId} className="mt-1 text-xs text-ink-2">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} role="alert" className="mt-1 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export function Checkbox({
  label,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  const id = useId();
  return (
    <div className={cn("flex items-start gap-2", className)}>
      <input id={rest.id ?? id} type="checkbox" className="mt-0.5" {...rest} />
      <label htmlFor={rest.id ?? id} className="cursor-pointer text-sm text-ink select-none">
        {label}
      </label>
    </div>
  );
}
